import { BffUnauthenticatedError, bffJson } from "@/shared/api/client";
import { currentSession } from "@/shared/api/session";

export class BffNoAdvisorError extends Error {
  readonly code = "NO_ADVISOR" as const;

  constructor() {
    super("This login represents no financial advisor");
    this.name = "BffNoAdvisorError";
  }
}

export class BffIdentityIntegrityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BffIdentityIntegrityError";
  }
}

type UserRepresentation =
  | { kind: "financial-advisor"; financialAdvisorId: string }
  | { kind: "individual-client"; individualClientId: string }
  | { kind: "corporation-client"; corporationClientId: string };

export interface AdvisorProfile {
  financialAdvisorId: string;
  individualId: string;
  corporationId: string;
  firstName: string;
  lastName: string;
  corporationName: string;
}

export interface BffIdentity {
  userId: string;
  userName: string;
  displayName: string | null;
  email: string | null;
  financialAdvisorId: string | null;
  corporationId: string | null;
  individualClientId: string | null;
  corporationClientId: string | null;
  languageId: string | null;
  activeRepresentation: UserRepresentation | null;
  alternativeRepresentations: UserRepresentation[];
  advisorProfile: AdvisorProfile | null;
}

interface UserRepresentationResponse {
  individual_client_id?: string | null;
  corporation_client_id?: string | null;
  financial_advisor_id?: string | null;
}

interface UserResponse {
  id: string;
  name?: string | null;
  display_name?: string | null;
  contact_email?: string | null;
  active_representation?: UserRepresentationResponse | null;
  alternative_representations?: UserRepresentationResponse[] | null;
  setting?: { language_id?: string | null } | null;
}

interface FinancialAdvisorResponse {
  id: string;
  individual_id?: string | null;
  corporation_id?: string | null;
}

interface IndividualResponse {
  first_name?: string | null;
  last_name?: string | null;
}

interface CorporationResponse {
  name?: string | null;
}

interface CurrentUserIdResponse {
  user_id: string;
}

let cache: { userId: string; promise: Promise<BffIdentity> } | null = null;

async function load(userId: string, userName: string): Promise<BffIdentity> {
  const id = userId || (await bffJson<CurrentUserIdResponse>("v1/auth/pang")).user_id;
  const user = await bffJson<UserResponse>(`v1/user/${encodeURIComponent(id)}`);
  const activeRepresentation = user.active_representation
    ? mapRepresentation(user.active_representation, "active representation")
    : null;
  if (!Array.isArray(user.alternative_representations)) {
    throw new BffIdentityIntegrityError("The user response has no alternative representations");
  }
  const alternativeRepresentations = user.alternative_representations.map((representation, index) =>
    mapRepresentation(representation, `alternative representation ${index + 1}`),
  );
  const financialAdvisorId =
    activeRepresentation?.kind === "financial-advisor"
      ? activeRepresentation.financialAdvisorId
      : null;
  const advisorProfile = financialAdvisorId ? await loadAdvisorProfile(financialAdvisorId) : null;

  const individualClientId =
    activeRepresentation?.kind === "individual-client"
      ? activeRepresentation.individualClientId
      : null;
  const corporationClientId =
    activeRepresentation?.kind === "corporation-client"
      ? activeRepresentation.corporationClientId
      : null;

  return {
    userId: id,
    userName: user.name ?? userName,
    displayName: user.display_name ?? null,
    email: user.contact_email ?? null,
    financialAdvisorId,
    corporationId: advisorProfile?.corporationId ?? null,
    individualClientId,
    corporationClientId,
    languageId: user.setting?.language_id ?? null,
    activeRepresentation,
    alternativeRepresentations,
    advisorProfile,
  };
}

export async function loadAdvisorProfile(financialAdvisorId: string): Promise<AdvisorProfile> {
  const advisor = await bffJson<FinancialAdvisorResponse>(
    `v1/financial-advisor/${encodeURIComponent(financialAdvisorId)}`,
  );
  const individualId = required(advisor.individual_id, "financial advisor individual_id");
  const corporationId = required(advisor.corporation_id, "financial advisor corporation_id");
  const [individual, corporation] = await Promise.all([
    bffJson<IndividualResponse>(`v1/individual/${encodeURIComponent(individualId)}`),
    bffJson<CorporationResponse>(`v1/corporation/${encodeURIComponent(corporationId)}`),
  ]);
  return {
    financialAdvisorId,
    individualId,
    corporationId,
    firstName: required(individual.first_name, "advisor first_name"),
    lastName: required(individual.last_name, "advisor last_name"),
    corporationName: required(corporation.name, "advisor corporation name"),
  };
}

function mapRepresentation(
  response: UserRepresentationResponse,
  label: string,
): UserRepresentation {
  const individualClientId = nonEmpty(response.individual_client_id);
  const corporationClientId = nonEmpty(response.corporation_client_id);
  const financialAdvisorId = nonEmpty(response.financial_advisor_id);
  const count = [individualClientId, corporationClientId, financialAdvisorId].filter(
    Boolean,
  ).length;
  if (count !== 1) {
    throw new BffIdentityIntegrityError(`${label} must contain exactly one representation id`);
  }
  if (financialAdvisorId) return { kind: "financial-advisor", financialAdvisorId };
  if (individualClientId) return { kind: "individual-client", individualClientId };
  return { kind: "corporation-client", corporationClientId: corporationClientId as string };
}

function required(value: string | null | undefined, label: string): string {
  const normalized = nonEmpty(value);
  if (!normalized) throw new BffIdentityIntegrityError(`Missing ${label}`);
  return normalized;
}

function nonEmpty(value: string | null | undefined): string | null {
  const normalized = value?.trim() ?? "";
  return normalized === "" ? null : normalized;
}

export function getIdentity(): Promise<BffIdentity> {
  const session = currentSession();
  if (!session) return Promise.reject(new BffUnauthenticatedError());
  if (cache && cache.userId === session.userId) return cache.promise;

  const promise = load(session.userId, session.userName);
  const entry = { userId: session.userId, promise };
  cache = entry;
  promise.catch(() => {
    if (cache === entry) cache = null;
  });
  return promise;
}

export type AdvisorIdentity = BffIdentity & { financialAdvisorId: string };

export async function requireAdvisor(): Promise<AdvisorIdentity> {
  const identity = await getIdentity();
  if (!identity.financialAdvisorId) throw new BffNoAdvisorError();
  return identity as AdvisorIdentity;
}

export function refreshIdentity(): Promise<BffIdentity> {
  cache = null;
  return getIdentity();
}

export function forgetIdentity(): void {
  cache = null;
}
